import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, Phone, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { solutionCategories } from '../data/solutionCategories';
import { findProductBySlug } from '../data/catalog';
import SEOHead from '../components/seo/SEOHead';
import Breadcrumbs from '../components/seo/Breadcrumbs';
import InternalLinks from '../components/seo/InternalLinks';
import GoogleReviewsStrip from '../components/GoogleReviewsStrip';
import CapacityStrip from '../components/CapacityStrip';
import { JsonLd, faqSchema } from '../components/seo/schemas';
import NotFound from './NotFound';

export default function SolutionCategoryPage() {
  const { slug } = useParams();
  const cat = solutionCategories[slug];
  if (!cat) return <NotFound />;

  const path = `/solutions/${slug}`;
  const products = cat.productSlugs.map(findProductBySlug).filter(Boolean);

  return (
    <div className="min-h-screen" data-testid="solution-category-page">
      <SEOHead title={cat.metaTitle} description={cat.metaDescription} path={path} image={cat.heroImage} />
      {cat.faqs && <JsonLd data={faqSchema(cat.faqs)} />}
      <Breadcrumbs items={[{ label: 'Solutions', to: '/products' }, { label: cat.name }]} />

      {/* Hero */}
      <section className="relative bg-alden-navy text-white overflow-hidden">
        <img
          src={cat.heroImage}
          alt={`${cat.name} by Alden Technologies`}
          className="absolute inset-0 w-full h-full object-cover opacity-25"
        />
        <div className="relative container mx-auto px-4 py-20 md:py-28">
          <div className="max-w-3xl">
            <div className="inline-block bg-alden-green px-4 py-1.5 rounded-full text-sm font-semibold mb-4">
              Designed. Fabricated. Tested. Delivered.
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold mb-5">{cat.h1}</h1>
            <p className="text-lg text-white/85 mb-8">{cat.intro}</p>
            <div className="flex flex-wrap gap-3">
              <Link to="/contact" data-testid="category-quote-cta">
                <Button className="bg-alden-green hover:bg-alden-green-light text-white py-6 px-7 text-base font-semibold">
                  Get Project Quotation <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <a href="tel:+919351292512">
                <Button className="bg-white text-alden-navy hover:bg-gray-100 py-6 px-7 text-base font-semibold">
                  <Phone className="mr-2 w-4 h-4" /> Talk to an Engineer
                </Button>
              </a>
              <a
                href={`https://wa.me/919351292512?text=${encodeURIComponent(`Hi, I need details about ${cat.name}`)}`}
                target="_blank" rel="noopener noreferrer"
              >
                <Button className="bg-[#25D366] hover:bg-[#1ebe5b] text-white py-6 px-7 text-base font-semibold">
                  <MessageCircle className="mr-2 w-4 h-4" /> WhatsApp Us
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {cat.showCapacities && <CapacityStrip />}

      {/* Product Grid */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-10">Our {cat.name} Range</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {products.map((p) => (
              <Link key={p.id} to={`/products/${p.slug}`} data-testid={`category-product-${p.slug}`}>
                <Card className="border-2 border-gray-200 hover:border-alden-green hover:shadow-xl transition-all h-full group overflow-hidden bg-white">
                  <div className="h-44 overflow-hidden">
                    <img
                      src={p.heroImage}
                      alt={`${p.title} manufactured by Alden Technologies`}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <CardContent className="p-5">
                    <div className="text-xs font-semibold text-alden-green uppercase tracking-wide mb-1">{p.capacity}</div>
                    <h3 className="font-bold text-gray-900 mb-2 group-hover:text-alden-green transition-colors">{p.title}</h3>
                    <p className="text-sm font-semibold text-gray-700 mb-3">{p.price?.range}</p>
                    <span className="inline-flex items-center gap-1 text-sm text-alden-green font-semibold">
                      View Specifications <ArrowRight className="w-4 h-4" />
                    </span>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      {cat.faqs && (
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-3xl">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Frequently Asked Questions</h2>
            <div className="space-y-5">
              {cat.faqs.map((f) => (
                <Card key={f.q} className="border-2 border-gray-200 hover:border-alden-green transition-all">
                  <CardContent className="p-6">
                    <h3 className="font-bold text-lg text-gray-900 mb-2">{f.q}</h3>
                    <p className="text-gray-700">{f.a}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      <GoogleReviewsStrip />
      <InternalLinks currentPath={path} />
    </div>
  );
}
